<template>
  <div class="ems-monitor-page" v-loading="loading">
    <!-- 数据概览 -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-icon income">
          <i class="ykc-icon-money"></i>
        </div>
        <div class="stat-info">
          <div class="stat-label">日收益/元</div>
          <div class="stat-value">{{ stats.dailyIncome }}</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-info">
          <div class="stat-label">总收益/元</div>
          <div class="stat-value">{{ stats.totalIncome }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon purchase">
          <i class="ykc-icon-flash"></i>
        </div>
        <div class="stat-info">
          <div class="stat-label">日购电量(kWh)</div>
          <div class="stat-value">{{ stats.dailyPurchasePower }}</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-info">
          <div class="stat-label">总购电量(kWh)</div>
          <div class="stat-value">{{ stats.totalPurchasePower }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon self-use">
          <i class="ykc-icon-flash"></i>
        </div>
        <div class="stat-info">
          <div class="stat-label">日自耗电量(kWh)</div>
          <div class="stat-value">{{ stats.dailySelfUsePower }}</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-info">
          <div class="stat-label">总自耗电量(kWh)</div>
          <div class="stat-value">{{ stats.totalSelfUsePower }}</div>
        </div>
      </div>
    </div>

    <!-- 主体内容区 -->
    <div class="main-content">
      <!-- 左侧 -->
      <div class="left-panel">
        <!-- 系统运行状态 -->
        <div class="panel-card">
          <div class="panel-header">
            <span class="panel-dot"></span>
            <span class="panel-title">系统运行</span>
          </div>
          <div class="system-status">
            <div class="status-row">
              <span class="status-label">BMS运行状态</span>
              <span :class="['status-value', systemStatus.bmsRunning ? 'running' : 'stopped']">
                {{ systemStatus.bmsStatusText }}
              </span>
            </div>
            <div class="status-row">
              <span class="status-label">PCS运行状态</span>
              <span :class="['status-value', systemStatus.pcsRunning ? 'running' : 'stopped']">
                {{ systemStatus.pcsStatusText }}
                <span :class="['status-value', systemStatus.pcsOnGrid ? 'running' : 'stopped']">
                  {{ systemStatus.pcsOnGridText }}
                </span>
              </span>
            </div>
          </div>
        </div>

        <!-- 系统日功率曲线 -->
        <div class="panel-card chart-card">
          <div class="panel-header">
            <span class="panel-dot"></span>
            <span class="panel-title">系统日功率曲线</span>
          </div>
          <div class="chart-legend">
            <span class="legend-item">
              <i class="legend-icon" style="background: #4d8cfd"></i>
              有功功率/kW
            </span>
            <span class="legend-item">
              <i class="legend-icon" style="background: #02c498"></i>
              无功功率/kvar
            </span>
            <span class="legend-item">
              <i class="legend-icon" style="background: #f5a623"></i>
              视在功率/kVA
            </span>
          </div>
          <div ref="powerChart" class="chart-container"></div>
        </div>
      </div>

      <!-- 中间拓扑图 -->
      <div class="center-panel">
        <div class="topology-container">
          <svg class="flow-svg" viewBox="0 0 480 420" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="batteryGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style="stop-color: #02c498; stop-opacity: 1" />
                <stop offset="100%" style="stop-color: #4d8cfd; stop-opacity: 1" />
              </linearGradient>
              <linearGradient id="baseGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style="stop-color: #b0c4de; stop-opacity: 0.8" />
                <stop offset="100%" style="stop-color: #e8eef5; stop-opacity: 0.9" />
              </linearGradient>
            </defs>

            <!-- ===== 连接线 ===== -->
            <!-- 电网→交流母线 竖线 -->
            <line
              x1="240"
              y1="80"
              x2="240"
              y2="120"
              stroke="#4d8cfd"
              stroke-width="2"
              stroke-dasharray="5,3" />
            <!-- 交流母线 横线（宽） -->
            <line x1="30" y1="120" x2="450" y2="120" stroke="#4d8cfd" stroke-width="3" />
            <!-- 母线节点圆点 -->
            <circle cx="30" cy="120" r="3" fill="#4d8cfd" />
            <circle cx="150" cy="120" r="3" fill="#4d8cfd" />
            <circle cx="240" cy="120" r="3" fill="#4d8cfd" />
            <circle cx="370" cy="120" r="3" fill="#4d8cfd" />
            <circle cx="450" cy="120" r="3" fill="#4d8cfd" />
            <!-- 母线→PCS 竖线 -->
            <line
              x1="150"
              y1="120"
              x2="150"
              y2="175"
              stroke="#4d8cfd"
              stroke-width="2"
              stroke-dasharray="5,3" />
            <!-- PCS→电池 竖线 -->
            <line
              x1="150"
              y1="265"
              x2="150"
              y2="305"
              stroke="#02c498"
              stroke-width="2"
              stroke-dasharray="5,3" />
            <!-- 母线→负荷 竖线 -->
            <line
              x1="370"
              y1="120"
              x2="370"
              y2="305"
              stroke="#f5a623"
              stroke-width="2"
              stroke-dasharray="5,3" />

            <!-- ===== 电网（双电塔） ===== -->
            <g transform="translate(190, 5)">
              <!-- 左塔 -->
              <path
                d="M15,68 L3,28 L12,28 L7,10 L28,10 L23,28 L32,28 L20,68 Z"
                fill="none"
                stroke="#4d8cfd"
                stroke-width="1.5" />
              <line x1="8" y1="28" x2="27" y2="28" stroke="#4d8cfd" stroke-width="1.2" />
              <line x1="11" y1="19" x2="24" y2="19" stroke="#4d8cfd" stroke-width="1.2" />
              <!-- 右塔 -->
              <path
                d="M75,68 L63,28 L72,28 L67,10 L88,10 L83,28 L92,28 L80,68 Z"
                fill="none"
                stroke="#4d8cfd"
                stroke-width="1.5" />
              <line x1="68" y1="28" x2="87" y2="28" stroke="#4d8cfd" stroke-width="1.2" />
              <line x1="71" y1="19" x2="84" y2="19" stroke="#4d8cfd" stroke-width="1.2" />
              <!-- 塔间连线 -->
              <line
                x1="17"
                y1="10"
                x2="70"
                y2="10"
                stroke="#4d8cfd"
                stroke-width="1"
                stroke-dasharray="3,2" />
              <line
                x1="20"
                y1="14"
                x2="67"
                y2="14"
                stroke="#4d8cfd"
                stroke-width="1"
                stroke-dasharray="3,2" />
            </g>

            <!-- 电网功率 -->
            <text
              x="240"
              y="90"
              text-anchor="middle"
              fill="#02c498"
              font-size="14"
              font-weight="bold"
              font-family="DIN Alternate, Helvetica Neue, Arial, sans-serif">
              {{ topology.gridPower }} kW
            </text>

            <!-- ===== 交流母线标签（左侧） ===== -->
            <text
              x="45"
              y="112"
              text-anchor="start"
              fill="#f5a623"
              font-size="13"
              font-weight="bold">
              交流母线
            </text>

            <!-- ===== PCS 变流器 ===== -->
            <!-- PCS功率（左侧） -->
            <text
              x="85"
              y="225"
              text-anchor="end"
              fill="#f5a623"
              font-size="13"
              font-weight="bold"
              font-family="DIN Alternate, Helvetica Neue, Arial, sans-serif">
              {{ topology.acPower }} kW
            </text>

            <!-- PCS 方框 -->
            <rect
              x="105"
              y="175"
              width="90"
              height="90"
              rx="8"
              fill="rgba(77,140,253,0.05)"
              stroke="#4d8cfd"
              stroke-width="1.5" />
            <!-- AC 文字 -->
            <text
              x="130"
              y="205"
              text-anchor="middle"
              fill="#4d8cfd"
              font-size="18"
              font-weight="bold">
              AC
            </text>
            <!-- 斜向箭头 ↗ -->
            <line x1="133" y1="240" x2="168" y2="200" stroke="#303133" stroke-width="2" />
            <polygon points="168,200 160,202 166,208" fill="#303133" />
            <!-- DC 文字 -->
            <text
              x="172"
              y="250"
              text-anchor="middle"
              fill="#4d8cfd"
              font-size="18"
              font-weight="bold">
              DC
            </text>

            <!-- ===== 电池 ===== -->
            <g transform="translate(95, 305)">
              <!-- 电池标签（左侧） -->
              <text
                x="-10"
                y="15"
                text-anchor="end"
                fill="#f5a623"
                font-size="13"
                font-weight="bold">
                电池
              </text>
              <text
                x="-10"
                y="35"
                text-anchor="end"
                fill="#02c498"
                font-size="15"
                font-weight="bold"
                font-family="DIN Alternate, Helvetica Neue, Arial, sans-serif">
                {{ topology.batterySoc }} %
              </text>
              <text
                x="-10"
                y="55"
                text-anchor="end"
                fill="#02c498"
                font-size="13"
                font-weight="bold"
                font-family="DIN Alternate, Helvetica Neue, Arial, sans-serif">
                {{ topology.batteryPower }} kW
              </text>
              <!-- 圆柱电池1 -->
              <rect
                x="10"
                y="5"
                width="40"
                height="50"
                rx="6"
                fill="rgba(77,140,253,0.06)"
                stroke="#4d8cfd"
                stroke-width="1.2" />
              <ellipse
                cx="30"
                cy="5"
                rx="20"
                ry="6"
                fill="rgba(77,140,253,0.1)"
                stroke="#4d8cfd"
                stroke-width="1" />
              <rect
                x="14"
                y="12"
                :width="(32 * (Number(topology.batterySoc) || 0)) / 100"
                height="36"
                rx="3"
                fill="url(#batteryGrad)"
                opacity="0.5" />
              <!-- 圆柱电池2 -->
              <rect
                x="60"
                y="5"
                width="40"
                height="50"
                rx="6"
                fill="rgba(77,140,253,0.06)"
                stroke="#4d8cfd"
                stroke-width="1.2" />
              <ellipse
                cx="80"
                cy="5"
                rx="20"
                ry="6"
                fill="rgba(77,140,253,0.1)"
                stroke="#4d8cfd"
                stroke-width="1" />
              <rect
                x="64"
                y="12"
                :width="(32 * (Number(topology.batterySoc) || 0)) / 100"
                height="36"
                rx="3"
                fill="url(#batteryGrad)"
                opacity="0.5" />
              <!-- 底座 -->
              <rect
                x="5"
                y="58"
                width="100"
                height="10"
                rx="3"
                fill="url(#baseGrad)"
                stroke="#4d8cfd"
                stroke-width="0.5"
                opacity="0.8" />
            </g>

            <!-- ===== 用电负荷 ===== -->
            <g transform="translate(320, 305)">
              <!-- 标签（左侧） -->
              <text
                x="50"
                y="-8"
                text-anchor="middle"
                fill="#f5a623"
                font-size="13"
                font-weight="bold">
                用电负荷
              </text>
              <!-- 电脑显示器 -->
              <rect
                x="10"
                y="5"
                width="80"
                height="50"
                rx="5"
                fill="rgba(77,140,253,0.05)"
                stroke="#4d8cfd"
                stroke-width="1.2" />
              <rect x="16" y="10" width="68" height="36" rx="3" fill="rgba(77,140,253,0.03)" />
              <!-- 屏幕内容 -->
              <line
                x1="22"
                y1="18"
                x2="62"
                y2="18"
                stroke="#4d8cfd"
                stroke-width="1"
                opacity="0.5" />
              <line
                x1="22"
                y1="26"
                x2="55"
                y2="26"
                stroke="#4d8cfd"
                stroke-width="1"
                opacity="0.5" />
              <line
                x1="22"
                y1="34"
                x2="58"
                y2="34"
                stroke="#4d8cfd"
                stroke-width="1"
                opacity="0.5" />
              <!-- 底座 -->
              <rect
                x="5"
                y="58"
                width="90"
                height="10"
                rx="3"
                fill="url(#baseGrad)"
                stroke="#4d8cfd"
                stroke-width="0.5"
                opacity="0.8" />
              <!-- 功率 -->
              <text
                x="50"
                y="48"
                text-anchor="middle"
                fill="#f5a623"
                font-size="14"
                font-weight="bold"
                font-family="DIN Alternate, Helvetica Neue, Arial, sans-serif">
                {{ topology.loadPower }} kW
              </text>
            </g>

            <!-- ===== 底部信息 ===== -->
            <text x="240" y="405" text-anchor="middle" fill="#4d8cfd" font-size="11" opacity="0.7">
              时间: {{ dataTime || '--' }}
            </text>
          </svg>
        </div>
      </div>

      <!-- 右侧 -->
      <div class="right-panel">
        <!-- 储能电池统计 -->
        <div class="panel-card">
          <div class="panel-header">
            <span class="panel-dot"></span>
            <span class="panel-title">储能电池</span>
          </div>
          <div class="battery-stats">
            <div class="battery-stat-row">
              <div class="battery-stat-item">
                <div class="battery-stat-label">日充电量(kWh)</div>
                <div class="battery-stat-value charge">{{ batteryStats.dailyCharge }}</div>
              </div>
              <div class="battery-stat-item">
                <div class="battery-stat-label">日放电量(kWh)</div>
                <div class="battery-stat-value discharge">{{ batteryStats.dailyDischarge }}</div>
              </div>
            </div>
            <div class="battery-stat-row">
              <div class="battery-stat-item">
                <div class="battery-stat-label">总充电量(kWh)</div>
                <div class="battery-stat-value charge">{{ batteryStats.totalCharge }}</div>
              </div>
              <div class="battery-stat-item">
                <div class="battery-stat-label">总放电量(kWh)</div>
                <div class="battery-stat-value discharge">{{ batteryStats.totalDischarge }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 储能日SOC曲线 -->
        <div class="panel-card chart-card">
          <div class="panel-header">
            <span class="panel-dot"></span>
            <span class="panel-title">储能日SOC曲线</span>
          </div>
          <div class="chart-legend">
            <span class="legend-item">
              <i class="legend-icon" style="background: #4d8cfd"></i>
              储能设备soc(%)
            </span>
          </div>
          <div ref="socChart" class="chart-container"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import * as echarts from 'echarts';
  import { getEmsRealtimeData, queryEmsHistory } from '@/service/emsApis';
  import { formatNum, default24hLabels, singleLineOption, tripleLineOption } from '../utils';

  export default {
    name: 'EmsMonitor',
    data() {
      return {
        dataTime: '',
        stats: {
          dailyIncome: '--',
          totalIncome: '--',
          dailyPurchasePower: '--',
          totalPurchasePower: '--',
          dailySelfUsePower: '--',
          totalSelfUsePower: '--',
        },
        systemStatus: {
          bmsRunning: false,
          bmsStatusText: '--',
          pcsRunning: false,
          pcsStatusText: '--',
          pcsOnGrid: false,
          pcsOnGridText: '--',
        },
        topology: {
          gridPower: '--',
          acPower: '--',
          dcPower: '--',
          batterySoc: '--',
          batteryPower: '--',
          loadPower: '--',
        },
        batteryStats: {
          dailyCharge: '--',
          dailyDischarge: '--',
          totalCharge: '--',
          totalDischarge: '--',
        },
        powerChartInstance: null,
        socChartInstance: null,
        refreshTimer: null,
        loading: false,
      };
    },
    computed: {
      stationCode() {
        return this.$store.getters.emsStationCode;
      },
    },
    watch: {
      stationCode() {
        this.fetchAllData();
      },
    },
    mounted() {
      this.fetchAllData();
      // 每30秒自动刷新实时数据
      this.refreshTimer = setInterval(() => {
        this.fetchRealtimeData();
      }, 30000);
      window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
      clearInterval(this.refreshTimer);
      window.removeEventListener('resize', this.handleResize);
      if (this.powerChartInstance) {
        this.powerChartInstance.dispose();
      }
      if (this.socChartInstance) {
        this.socChartInstance.dispose();
      }
    },
    methods: {
      fetchAllData() {
        this.loading = true;
        Promise.all([
          this.fetchRealtimeData(),
          this.fetchIncomeData(),
          this.fetchPowerCurve(),
          this.fetchSocCurve(),
        ]).finally(() => {
          this.loading = false;
        });
      },

      async fetchRealtimeData() {
        try {
          const res = await getEmsRealtimeData(this.stationCode);
          if (!res) return;
          this.parseEmsRealtimeData(res);
        } catch (e) {
          console.error('获取EMS实时数据失败', e);
        }
      },

      async fetchIncomeData() {
        try {
          const res = await queryEmsHistory({
            proc: 'mny_load_site',
            param: [this.stationCode],
          });
          const list = Array.isArray(res) ? res : res?.data || [];
          if (list.length > 0) {
            const item = list[0];
            this.stats.dailyIncome = this.formatNum(item.mny);
            this.stats.totalIncome = this.formatNum(item.mny_sum);
          }
        } catch (e) {
          console.error('获取收益数据失败', e);
        }
      },

      parseEmsRealtimeData(data) {
        // 数据采集时间
        if (data.attm) {
          this.dataTime = dayjs(data.attm).format('YYYY-MM-DD HH:mm:ss');
        }
        // 数据概览 - 购电量来自电网数据
        this.stats.dailyPurchasePower = this.formatNum(data.eg_q_i);
        this.stats.totalPurchasePower = this.formatNum(data.eg_q_i_sum);
        // 自耗电量来自自耗电表
        this.stats.dailySelfUsePower = this.formatNum(data.zh_q);
        this.stats.totalSelfUsePower = this.formatNum(data.zh_q_sum);

        // 系统运行状态
        // BMS运行状态: bms_st_run 1=启动(绿) 0=停止(红)
        this.systemStatus.bmsRunning = data.bms_st_run === 1;
        this.systemStatus.bmsStatusText = this.getBmsStatusText(data.bms_st_run);
        // PCS运行状态: pcs_st_run 1=启动(绿) 0=停止 2=启动中(红)
        this.systemStatus.pcsRunning = data.pcs_st_run === 1;
        this.systemStatus.pcsStatusText = this.getPcsRunStatusText(data.pcs_st_run);
        // PCS并离网状态: pcs_st_oo 1=并网(绿) 0=无效 2=离网(红)
        this.systemStatus.pcsOnGrid = data.pcs_st_oo === 1;
        this.systemStatus.pcsOnGridText = this.getPcsOnGridText(data.pcs_st_oo);

        // 拓扑图 - 电网功率/交流侧/直流侧
        this.topology.gridPower = this.formatNum(data.eg_w);
        this.topology.acPower = this.formatNum(data.pcs_acw);
        this.topology.dcPower = this.formatNum(data.pcs_dcw);
        this.topology.batterySoc = data.bms_soc != null ? String(data.bms_soc) : '--';
        this.topology.batteryPower = this.formatNum(data.bms_w);
        this.topology.loadPower = this.formatNum(data.fz_w);

        // 储能电池统计 - 充电量/放电量
        this.batteryStats.dailyCharge = this.formatNum(data.bms_q_i);
        this.batteryStats.dailyDischarge = this.formatNum(data.bms_q_o);
        this.batteryStats.totalCharge = this.formatNum(data.bms_q_i_sum);
        this.batteryStats.totalDischarge = this.formatNum(data.bms_q_o_sum);
      },

      async fetchPowerCurve() {
        try {
          const date = dayjs().format('YYYY-MM-DD');
          const res = await queryEmsHistory({
            proc: 'pcs_m_chart',
            param: [this.stationCode, `${date} 00:00:00`, `${date} 23:59:59`],
          });
          this.renderPowerChart(res);
        } catch (e) {
          console.error('获取功率曲线失败', e);
        }
      },

      async fetchSocCurve() {
        try {
          const date = dayjs().format('YYYY-MM-DD');
          const res = await queryEmsHistory({
            proc: 'bms_m_chart',
            param: [this.stationCode, `${date} 00:00:00`, `${date} 23:59:59`],
          });
          this.renderSocChart(res);
        } catch (e) {
          console.error('获取SOC曲线失败', e);
        }
      },

      renderPowerChart(historyData) {
        const chartDom = this.$refs.powerChart;
        if (!chartDom) return;
        if (!this.powerChartInstance) {
          this.powerChartInstance = echarts.init(chartDom);
        }

        const xData = [];
        const activePowerData = [];
        const reactivePowerData = [];
        const apparentPowerData = [];

        const list = Array.isArray(historyData) ? historyData : historyData?.data || [];
        if (list.length > 0) {
          list.forEach(item => {
            xData.push(item.attm ? dayjs(item.attm).format('HH:mm') : '');
            activePowerData.push(item.acw1 ?? null);
            reactivePowerData.push(item.acw2 ?? null);
            apparentPowerData.push(item.acw3 ?? null);
          });
        } else {
          xData.push(...default24hLabels());
        }

        const option = tripleLineOption(
          ['有功功率/kW', '无功功率/kvar', '视在功率/kVA'],
          ['#4d8cfd', '#02c498', '#f5a623'],
          xData,
          [activePowerData, reactivePowerData, apparentPowerData]
        );
        this.powerChartInstance.setOption(option, true);
      },

      renderSocChart(historyData) {
        const chartDom = this.$refs.socChart;
        if (!chartDom) return;
        if (!this.socChartInstance) {
          this.socChartInstance = echarts.init(chartDom);
        }

        const xData = [];
        const socData = [];

        const list = Array.isArray(historyData) ? historyData : historyData?.data || [];
        if (list.length > 0) {
          list.forEach(item => {
            xData.push(item.attm ? dayjs(item.attm).format('HH:mm') : '');
            socData.push(item.soc ?? null);
          });
        } else {
          xData.push(...default24hLabels());
        }

        const socAreaStyle = {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(77, 140, 253, 0.3)' },
              { offset: 1, color: 'rgba(77, 140, 253, 0.02)' },
            ],
          },
        };
        const option = singleLineOption('储能设备soc(%)', '#4d8cfd', xData, socData, {
          areaStyle: socAreaStyle,
          yAxis: { min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
        });
        this.socChartInstance.setOption(option, true);
      },

      handleResize() {
        if (this.powerChartInstance) {
          this.powerChartInstance.resize();
        }
        if (this.socChartInstance) {
          this.socChartInstance.resize();
        }
      },

      formatNum,

      // BMS运行状态: 1=启动(绿) 0=停止(红)
      getBmsStatusText(val) {
        if (val === 1) return '启动';
        if (val === 0) return '停止';
        return '--';
      },

      // PCS运行状态: 0=停止(红) 1=启动(绿) 2=启动中(红)
      getPcsRunStatusText(val) {
        if (val === 1) return '启动';
        if (val === 0) return '停止';
        if (val === 2) return '启动中';
        return '--';
      },

      // PCS并离网状态: 0=无效(红) 1=并网(绿) 2=离网(红)
      getPcsOnGridText(val) {
        if (val === 1) return '并网';
        if (val === 0) return '无效';
        if (val === 2) return '离网';
        return '--';
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../styles/variables.scss';

  .ems-monitor-page {
    width: 100%;
    height: 100%;
    background: $bg-primary;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  // 顶部统计卡片
  .stats-cards {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .stat-card {
    flex: 1;
    min-width: 280px;
    background: #fff;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 16px;

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: #fff;
      flex-shrink: 0;

      &.income {
        background: linear-gradient(135deg, #4d8cfd, #386cc8);
      }
      &.purchase {
        background: linear-gradient(135deg, #02c498, #009c79);
      }
      &.self-use {
        background: linear-gradient(135deg, #f5a623, #dda446);
      }

      i {
        color: #fff;
      }
    }

    .stat-info {
      flex: 1;
    }

    .stat-label {
      font-size: 12px;
      color: $text-secondary;
      margin-bottom: 6px;
    }

    .stat-value {
      font-size: 22px;
      font-weight: bold;
      color: $text-primary;
      font-family: 'DIN Alternate', 'Helvetica Neue', Arial, sans-serif;
    }

    .stat-divider {
      width: 1px;
      height: 40px;
      background: $bg-card-border;
    }
  }

  // 主体内容
  .main-content {
    flex: 1;
    display: flex;
    gap: 16px;
    min-height: 0;
  }

  .left-panel,
  .right-panel {
    width: 33.33%;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .center-panel {
    width: 33.33%;
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
  }

  // 面板卡片
  .panel-card {
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
    padding: 12px 16px;

    &.chart-card {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }

  .panel-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;

    .panel-dot {
      width: 4px;
      height: 14px;
      background: $accent-blue;
      border-radius: 2px;
    }

    .panel-title {
      font-size: 14px;
      font-weight: 600;
      color: $text-primary;
    }
  }

  // 系统运行状态
  .system-status {
    .status-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 0;
      border-bottom: 1px solid $bg-card-border;

      &:last-child {
        border-bottom: none;
      }
    }

    .status-label {
      font-size: 13px;
      color: $text-secondary;
    }

    .status-value {
      font-size: 13px;
      font-weight: 500;
      color: $text-secondary;

      &.running {
        color: $accent-green;
      }

      &.stopped {
        color: $accent-red;
      }
    }
  }

  // 图表图例
  .chart-legend {
    display: flex;
    gap: 16px;
    margin-bottom: 8px;
    flex-wrap: wrap;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      color: $text-secondary;
    }

    .legend-icon {
      width: 12px;
      height: 3px;
      border-radius: 2px;
    }
  }

  .chart-container {
    flex: 1;
    min-height: 180px;
  }

  // 拓扑图
  .topology-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    .flow-svg {
      width: 100%;
      height: 100%;
      max-height: 420px;
    }
  }

  // 储能电池统计
  .battery-stats {
    .battery-stat-row {
      display: flex;
      gap: 12px;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .battery-stat-item {
      flex: 1;
      text-align: center;
      padding: 10px;
      background: #f6f7f8;
      border-radius: 6px;
    }

    .battery-stat-label {
      font-size: 11px;
      color: $text-secondary;
      margin-bottom: 6px;
    }

    .battery-stat-value {
      font-size: 18px;
      font-weight: bold;
      font-family: 'DIN Alternate', 'Helvetica Neue', Arial, sans-serif;

      &.charge {
        color: $accent-blue;
      }

      &.discharge {
        color: $accent-green;
      }
    }
  }
</style>
